/**
 * Countdown Timer Component
 *
 * Displays a countdown to a target date/time.
 * Color-coded based on urgency (green → yellow → red).
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Clock } from 'lucide-react';

interface CountdownTimerProps {
  /** Target date/time to count down to */
  targetDate: string | Date;
  /** Label text (optional) */
  label?: string;
  /** Warning threshold in minutes (default: 60) */
  warningMinutes?: number;
  /** Danger threshold in minutes (default: 15) */
  dangerMinutes?: number;
  /** Show icon */
  showIcon?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Custom class name */
  className?: string;
  /** Callback when countdown reaches zero */
  onExpired?: () => void;
}

interface TimeRemaining {
  hours: number;
  minutes: number;
  seconds: number;
  totalMinutes: number;
  isExpired: boolean;
}

function calculateTimeRemaining(target: Date): TimeRemaining {
  const now = new Date();
  const diff = target.getTime() - now.getTime();

  if (diff <= 0) {
    return { hours: 0, minutes: 0, seconds: 0, totalMinutes: 0, isExpired: true };
  }

  const totalSeconds = Math.floor(diff / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(diff / 60000);

  return { hours, minutes, seconds, totalMinutes, isExpired: false };
}

const SIZE_CONFIG = {
  sm: { text: 'text-sm', icon: 'w-3 h-3', label: 'text-xs' },
  md: { text: 'text-lg', icon: 'w-4 h-4', label: 'text-xs' },
  lg: { text: 'text-2xl', icon: 'w-5 h-5', label: 'text-sm' },
};

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetDate,
  label,
  warningMinutes = 60,
  dangerMinutes = 15,
  showIcon = true,
  size = 'md',
  className = '',
  onExpired,
}) => {
  const target = useMemo(
    () => (targetDate instanceof Date ? targetDate : new Date(targetDate)),
    [targetDate]
  );

  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(
    () => calculateTimeRemaining(target)
  );

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = calculateTimeRemaining(target);
      setTimeRemaining(remaining);

      if (remaining.isExpired) {
        clearInterval(timer);
        onExpired?.();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [target, onExpired]);

  // Determine color based on urgency
  let colorClass: string;
  if (timeRemaining.isExpired) {
    colorClass = 'text-red-400';
  } else if (timeRemaining.totalMinutes <= dangerMinutes) {
    colorClass = 'text-red-400';
  } else if (timeRemaining.totalMinutes <= warningMinutes) {
    colorClass = 'text-amber-400';
  } else {
    colorClass = 'text-emerald-400';
  }

  const config = SIZE_CONFIG[size];

  // Format time display
  const formatTime = () => {
    if (timeRemaining.isExpired) return 'Utl\u00f8pt';

    const parts: string[] = [];
    if (timeRemaining.hours > 0) {
      parts.push(`${timeRemaining.hours}t`);
    }
    parts.push(`${timeRemaining.minutes.toString().padStart(2, '0')}m`);
    parts.push(`${timeRemaining.seconds.toString().padStart(2, '0')}s`);

    return parts.join(' ');
  };

  return (
    <div className={`inline-flex flex-col ${className}`}>
      {label && (
        <span className={`${config.label} text-gray-400 mb-0.5`}>{label}</span>
      )}
      <div className={`inline-flex items-center gap-1.5 ${colorClass}`}>
        {showIcon && <Clock className={config.icon} />}
        <span className={`${config.text} font-mono font-bold tabular-nums`}>
          {formatTime()}
        </span>
      </div>
    </div>
  );
};

export default CountdownTimer;
